import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../utils/firebase';
import { LogoSVG } from '../../components/Icons';
import './ResetPassword.css';

type Estado = 'verificando' | 'listo' | 'invalido' | 'guardando' | 'exito';

const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const oobCode = params.get('oobCode') || '';

  const [estado, setEstado] = useState<Estado>('verificando');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!oobCode) { setEstado('invalido'); return; }
    verifyPasswordResetCode(auth, oobCode)
      .then((correo) => { setEmail(correo); setEstado('listo'); })
      .catch(() => setEstado('invalido'));
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 12) { setError('La contraseña debe tener al menos 12 caracteres.'); return; }
    if (password !== confirmar) { setError('Las contraseñas no coinciden.'); return; }

    setEstado('guardando');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setEstado('exito');
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
        setEstado('invalido');
      } else if (code === 'auth/weak-password') {
        setError('La contraseña es demasiado débil.');
        setEstado('listo');
      } else {
        setError('No se pudo restablecer la contraseña. Intente nuevamente.');
        setEstado('listo');
      }
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        <div className="reset-logo"><LogoSVG size={42} /></div>
        <h2 className="reset-title">Restablecer contraseña</h2>
        <p className="reset-subtitle">Souza &amp; Villa Abogadas — Panel de gestión</p>

        {estado === 'verificando' && (
          <p className="reset-info">Verificando enlace…</p>
        )}

        {estado === 'invalido' && (
          <div className="reset-invalido">
            <p>Este enlace no es válido o ya expiró.</p>
            <p className="reset-info">Solicite un nuevo enlace de recuperación desde la pantalla de inicio de sesión.</p>
            <Link to="/" className="btn-primary reset-btn">Volver al inicio →</Link>
          </div>
        )}

        {(estado === 'listo' || estado === 'guardando') && (
          <form onSubmit={handleSubmit} className="reset-form">
            <p className="reset-info">Cuenta: <strong>{email}</strong></p>
            <div className="form-group">
              <label className="form-label">Nueva contraseña</label>
              <input
                className="form-control reset-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 12 caracteres"
                required
                minLength={12}
                disabled={estado === 'guardando'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirmar contraseña</label>
              <input
                className="form-control reset-input"
                type="password"
                value={confirmar}
                onChange={(e) => setConfirmar(e.target.value)}
                placeholder="Repita la contraseña"
                required
                minLength={12}
                disabled={estado === 'guardando'}
              />
            </div>
            {error && <div className="reset-error">{error}</div>}
            <button type="submit" className="btn-primary reset-btn" disabled={estado === 'guardando'}>
              {estado === 'guardando' ? 'Guardando…' : 'Restablecer contraseña →'}
            </button>
          </form>
        )}

        {estado === 'exito' && (
          <div className="reset-exito">
            <div className="reset-exito-icon">✓</div>
            <p className="reset-exito-text">Contraseña actualizada</p>
            <p className="reset-info">Ya puede iniciar sesión con su nueva contraseña.</p>
            <Link to="/" className="btn-primary reset-btn">Ir al inicio →</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
